import { call, cancel, put, select, delay } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/types';
import { PayloadAction } from '@reduxjs/toolkit';
import { SubjectArea } from 'features/subjectArea/types';
import { getSubjectAreaPage } from './sagaUtils';
import { getCurrentSubjectId } from 'features/subjectPage/redux';
import {
	getCurrentSubjectNumericId,
	getSubjectAreaActiveExamLevel,
} from 'features/subjectsOverview/redux';
import {
	setCurrentSubjectIdSubjectArea,
	setSubjectAreaActiveExamLevel,
	setsubjectAreaLoading,
} from '../slice';
import {
	fetchSubjectAreaActiveExamLevel,
	fetchSubjectAreaSubjectId,
	getSelectedSubjectAreaComparisons,
} from '../selectors';
import { changeComprisonsFlow } from './changeComparisonFlow';
import { attainmentProfileData } from './fetchAttainmentProfileData';

/**
 * Saga that handles the changing of the applied subject
 */
export function* changeSubjectFlow(action?: PayloadAction<string>): SagaIterator<void> {
	// Set loading to true
	yield put(setsubjectAreaLoading(true));

	//Get the Current Subject area page
	const subjectAreaPage: SubjectArea.SubjectAnalysisPages = yield call(getSubjectAreaPage);

	//Fetch the subject page 'id' this is the string name not a numeric id but until more work is done on the subject req we need to use this to get the id
	// If the action is provided then use the action payload otherwise use the current subject id from the redux store
	const subjectPageCurrentSubject = action?.payload ?? (yield select(getCurrentSubjectId));

	//use the subject page 'id' to get the actual numeric id to be applied to the subject area redux
	const subjectOverviewSubjectId = yield select(
		getCurrentSubjectNumericId(subjectPageCurrentSubject)
	);

	// If the subject overview subject id is not found then set loading to false and cancel the saga
	if (!subjectOverviewSubjectId) {
		yield put(setsubjectAreaLoading(false));
		yield cancel();
	}

	//Get the subject page exam level
	const currentSubjectActiveExamlevel = yield select(
		getSubjectAreaActiveExamLevel(subjectPageCurrentSubject)
	);

	// Check if the subject id or exam level has changed since the last fetch
	const storedSubjectId = yield select(fetchSubjectAreaSubjectId);
	const storedExamLevel = yield select(fetchSubjectAreaActiveExamLevel);
	const skipRefetch =
		subjectOverviewSubjectId === storedSubjectId &&
		currentSubjectActiveExamlevel === storedExamLevel;

	//Fetch subject area comparisons
	const subjectAreaComparisons = yield select(getSelectedSubjectAreaComparisons);

	//update the subject area examlevel  - this will be 0 when fetched/ applied but will allow future reusuablity with the selectors
	yield put(setSubjectAreaActiveExamLevel(currentSubjectActiveExamlevel));

	//update the subject area subject id
	yield put(setCurrentSubjectIdSubjectArea(subjectOverviewSubjectId));

	if (!skipRefetch) {
		// Fetch attainment grid/graph if the subject id or exam level has changed
		yield call(
			attainmentProfileData,
			subjectAreaPage,
			currentSubjectActiveExamlevel,
			subjectOverviewSubjectId
		);
	}

	//if theres comaprisons spawn comparison flow
	if (subjectAreaComparisons.length > 0) {
		yield call(changeComprisonsFlow);
	}

	//Allow state to update
	yield delay(1000);

	//Set subject area loading to false
	yield put(setsubjectAreaLoading(false));
}
